import { completeMockDocumentUpload } from './mock-transport';

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

export interface PresignedUploadInput {
  url: string;
  file: File;
  requiredHeaders?: Record<string, string>;
  documentId: string;
  signal?: AbortSignal;
  onProgress?: (progress: UploadProgress) => void;
}

export class UploadTransportError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = 'UploadTransportError';
  }
}

export function uploadPresignedFile(input: PresignedUploadInput): Promise<void> {
  if (input.url.startsWith('mock://')) return simulateMockUpload(input);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let settled = false;

    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      input.signal?.removeEventListener('abort', abort);
      callback();
    };
    const abort = () => {
      if (settled) return;
      xhr.abort();
    };

    if (input.signal?.aborted) {
      reject(new UploadTransportError('The document upload was cancelled.'));
      return;
    }

    xhr.open('PUT', input.url, true);
    Object.entries(input.requiredHeaders ?? {}).forEach(([name, value]) => xhr.setRequestHeader(name, value));
    input.signal?.addEventListener('abort', abort, { once: true });

    xhr.upload.onprogress = (event) => {
      const total = event.lengthComputable ? event.total : input.file.size;
      const percent = total > 0 ? Math.min(100, Math.round((event.loaded / total) * 100)) : 0;
      input.onProgress?.({ loaded: event.loaded, total, percent });
    };
    xhr.onerror = () => finish(() => reject(new UploadTransportError('The document upload was interrupted. Check your connection and try again.')));
    xhr.onabort = () => finish(() => reject(new UploadTransportError('The document upload was cancelled.')));
    xhr.onload = () => finish(() => {
      if (xhr.status >= 200 && xhr.status < 300) {
        input.onProgress?.({ loaded: input.file.size, total: input.file.size, percent: 100 });
        resolve();
        return;
      }
      reject(new UploadTransportError(`The document upload failed with HTTP ${xhr.status}.`, xhr.status));
    });
    xhr.send(input.file);
  });
}

async function simulateMockUpload(input: PresignedUploadInput): Promise<void> {
  for (const percent of [12, 34, 61, 84, 100]) {
    if (input.signal?.aborted) throw new UploadTransportError('The document upload was cancelled.');
    await abortableDelay(70, input.signal);
    const loaded = Math.round(input.file.size * (percent / 100));
    input.onProgress?.({ loaded, total: input.file.size, percent });
  }
  completeMockDocumentUpload(input.documentId);
}

function abortableDelay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new UploadTransportError('The document upload was cancelled.'));
      return;
    }
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', cancel);
      resolve();
    }, ms);
    const cancel = () => {
      window.clearTimeout(timer);
      reject(new UploadTransportError('The document upload was cancelled.'));
    };
    signal?.addEventListener('abort', cancel, { once: true });
  });
}
